import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Clock, FileText, Sparkles, Briefcase } from "lucide-react";
import { Input } from "@/components/ui/input";
import LegalDisclaimer from "@/components/LegalDisclaimer";

// Stable module-level constants so hooks can safely omit them from dep arrays.
const STORAGE_KEY = "bbp_exam_state_v1";
const DEADLINE_KEY = "bbp_exam_deadline_v1";
const MCQ_DURATION_MS = 30 * 60 * 1000; // 30 minutes

export default function Exam() {
  const navigate = useNavigate();
  const [state, setState] = useState("start"); // start | running | profession | case_study | submitting
  const [attemptId, setAttemptId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [caseStudy, setCaseStudy] = useState(null);
  const [caseStudyAnswer, setCaseStudyAnswer] = useState("");
  const [profession, setProfession] = useState("");
  const [generatingCase, setGeneratingCase] = useState(false);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [passScore, setPassScore] = useState(80);
  const [loading, setLoading] = useState(false);
  const [deadline, setDeadline] = useState(null); // ms epoch
  const [now, setNow] = useState(Date.now());

  // ---- Persistence: save/restore exam state to localStorage ----
  // (STORAGE_KEY, DEADLINE_KEY, MCQ_DURATION_MS are module-level constants above)

  // Restore on mount if an unsubmitted attempt exists
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved.state && saved.attemptId && saved.state !== "start") {
        setAttemptId(saved.attemptId);
        setQuestions(saved.questions || []);
        setCaseStudy(saved.caseStudy || null);
        setCaseStudyAnswer(saved.caseStudyAnswer || "");
        setProfession(saved.profession || "");
        setAnswers(saved.answers || {});
        setCurrentIdx(saved.currentIdx || 0);
        setPassScore(saved.passScore || 80);
        setState(saved.state);
        const savedDeadline = parseInt(localStorage.getItem(DEADLINE_KEY) || "0", 10);
        if (savedDeadline > 0) setDeadline(savedDeadline);
        toast.success("Your exam has been restored where you left off.");
      }
    } catch (err) {
      console.error("Failed to restore exam state", err);
    }
  }, []);

  // Tick every second while MCQs are being taken
  useEffect(() => {
    if (state !== "running" || !deadline) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state, deadline]);

  // Auto-advance to profession when MCQ timer expires
  useEffect(() => {
    if (state !== "running" || !deadline) return;
    if (now >= deadline) {
      toast.warning("MCQ time is up — proceeding to your case study.");
      setState("profession");
      window.scrollTo({ top: 0 });
    }
  }, [now, deadline, state]);

  // Save on any state/answer/idx change
  useEffect(() => {
    if (state === "start") return;
    if (state === "submitting") return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        state, attemptId, questions, caseStudy, caseStudyAnswer, profession, answers, currentIdx, passScore,
      }));
    } catch (err) {
      console.error("Failed to persist exam state", err);
    }
  }, [state, attemptId, questions, caseStudy, caseStudyAnswer, profession, answers, currentIdx, passScore]);

  // Warn on page unload / refresh while in the middle of an attempt
  useEffect(() => {
    const inProgress = state === "running" || state === "profession" || state === "case_study";
    if (!inProgress) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "Your exam is in progress. Are you sure you want to leave?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [state]);

  const clearSaved = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(DEADLINE_KEY);
    } catch (err) {
      console.error("Failed to clear exam state", err);
    }
  };

  const startExam = async () => {
    setLoading(true);
    try {
      clearSaved();
      const res = await api.post("/exam/start");
      setAttemptId(res.data.attempt_id);
      setQuestions(res.data.questions);
      setCaseStudy(null);
      setCaseStudyAnswer("");
      setProfession("");
      setPassScore(res.data.pass_score);
      setState("running");
      setCurrentIdx(0);
      setAnswers({});
      const dl = Date.now() + MCQ_DURATION_MS;
      setDeadline(dl);
      setNow(Date.now());
      try { localStorage.setItem(DEADLINE_KEY, String(dl)); } catch (err) { console.error("Deadline persist failed", err); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start exam");
    } finally {
      setLoading(false);
    }
  };

  const goToProfession = () => {
    setState("profession");
    window.scrollTo({ top: 0 });
  };

  const generateCaseStudy = async () => {
    const p = profession.trim();
    if (!p) {
      toast.error("Please enter your profession");
      return;
    }
    setGeneratingCase(true);
    try {
      const res = await api.post("/exam/case-study/generate", {
        attempt_id: attemptId,
        profession: p,
      });
      setCaseStudy(res.data);
      setState("case_study");
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate case study");
    } finally {
      setGeneratingCase(false);
    }
  };

  const submitExam = async () => {
    setState("submitting");
    try {
      const res = await api.post("/exam/submit", {
        attempt_id: attemptId,
        answers,
        case_study_answer: caseStudyAnswer.trim() || null,
      });
      sessionStorage.setItem(`exam_result_${attemptId}`, JSON.stringify(res.data));
      clearSaved();
      navigate(`/result/${attemptId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
      setState("case_study");
    }
  };

  const current = questions[currentIdx];
  const answered = Object.keys(answers).length;

  if (state === "start") {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-3">Final assessment</div>
          <h1 className="font-display text-5xl font-black tracking-tight">Ready for the exam?</h1>
          <p className="text-zinc-600 mt-4 text-lg leading-relaxed max-w-xl">
            Two parts: <strong>50 multiple-choice questions</strong> plus a real-world <strong>AI-graded case study</strong>.
            A combined score of <strong className="text-red-600">80% or higher</strong> earns your IMCAC certificate.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="border-2 border-zinc-900 bg-white p-5 rounded-lg" data-testid="exam-info-questions">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">MCQs</div>
              <div className="font-display text-3xl font-bold mt-1">50</div>
              <div className="text-xs text-zinc-500 mt-1">30 min timer · 70% weight</div>
            </div>
            <div className="border-2 border-zinc-900 bg-white p-5 rounded-lg" data-testid="exam-info-case-study">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Case study</div>
              <div className="font-display text-3xl font-bold mt-1 flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-red-600" /> AI
              </div>
              <div className="text-xs text-zinc-500 mt-1">Claude Sonnet 4.5 · 30% weight</div>
            </div>
            <div className="border-2 border-zinc-900 bg-white p-5 rounded-lg" data-testid="exam-info-pass">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Pass score</div>
              <div className="font-display text-3xl font-bold mt-1">80%</div>
              <div className="text-xs text-zinc-500 mt-1">Combined</div>
            </div>
            <div className="border-2 border-zinc-900 bg-white p-5 rounded-lg" data-testid="exam-info-retry">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Retries</div>
              <div className="font-display text-3xl font-bold mt-1">∞</div>
              <div className="text-xs text-zinc-500 mt-1">Retake anytime</div>
            </div>
          </div>

          <div className="mt-10 flex gap-3">
            <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="exam-back-btn">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button
              onClick={startExam}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="exam-start-btn"
            >
              {loading ? "Loading…" : "Start exam"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (state === "profession") {
    const suggestions = [
      "Registered Nurse", "Phlebotomist", "EMT / Paramedic", "School Nurse",
      "Dental Hygienist", "Tattoo Artist", "Piercing Artist", "Custodial Staff",
      "Lab Technician", "Firefighter", "Correctional Officer",
      "Esthetician", "Spa Therapist", "Semi-Permanent Makeup Artist",
      "Salon Assistant", "Homemaker",
      "Sports Teacher", "Fitness Trainer", "Sports Coach",
      "Massage Therapist", "Acupuncturist", "Veterinary Assistant",
      "Childcare Worker", "Security Officer", "Housekeeper",
      "Other",
    ];
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Navbar />
        <div className="border-b border-zinc-200 bg-white">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
              Part 2 of 2 · Profession
            </div>
            <div className="text-xs font-mono uppercase tracking-widest text-red-600 flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> AI will tailor your case study
            </div>
          </div>
          <div className="h-1 bg-zinc-100">
            <div className="h-full bg-red-600" style={{ width: "80%" }} />
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-6 py-16" data-testid="profession-screen">
          <div className="flex items-center gap-3 mb-4">
            <Briefcase className="h-6 w-6 text-red-600" />
            <div className="text-xs font-mono uppercase tracking-widest text-red-600">Before we continue</div>
          </div>

          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight leading-tight">
            What's your profession?
          </h1>
          <p className="text-zinc-600 mt-4 text-lg leading-relaxed max-w-xl">
            Claude Sonnet 4.5 will use this to generate a realistic Bloodborne Pathogens case study
            tailored to a scenario you might actually face in your workplace.
          </p>

          <div className="mt-10">
            <label htmlFor="profession-input" className="text-xs uppercase tracking-wider font-mono text-zinc-700">
              Your profession or job title
            </label>
            <Input
              id="profession-input"
              value={profession}
              onChange={(e) => setProfession(e.target.value)}
              placeholder="e.g. Registered Nurse, Tattoo Artist, EMT…"
              maxLength={120}
              disabled={generatingCase}
              className="mt-2 h-12 text-base"
              data-testid="profession-input"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && profession.trim() && !generatingCase) generateCaseStudy();
              }}
            />
          </div>

          <div className="mt-5">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Quick picks</div>
            <div className="flex flex-wrap gap-2" data-testid="profession-suggestions">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setProfession(s)}
                  disabled={generatingCase}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    profession === s
                      ? "border-red-600 bg-red-50 text-red-800 font-medium"
                      : "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-500"
                  }`}
                  data-testid={`profession-chip-${s.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-12 flex flex-wrap gap-3 justify-between items-center">
            <Button
              variant="outline"
              onClick={() => setState("running")}
              disabled={generatingCase}
              data-testid="profession-back-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to MCQs
            </Button>
            <Button
              onClick={generateCaseStudy}
              disabled={!profession.trim() || generatingCase}
              className="bg-red-600 hover:bg-red-700 text-white disabled:bg-zinc-300 disabled:text-zinc-500"
              data-testid="profession-generate-btn"
            >
              {generatingCase ? (
                <>
                  <Sparkles className="mr-2 h-4 w-4 animate-pulse" /> Generating your case study…
                </>
              ) : (
                <>
                  Generate case study <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (state === "case_study" || state === "submitting") {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Navbar />
        <div className="border-b border-zinc-200 bg-white">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
              Part 2 of 2 · Case study
            </div>
            <div className="text-xs font-mono uppercase tracking-widest text-red-600 flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> AI-graded by Claude Sonnet 4.5
            </div>
          </div>
          <div className="h-1 bg-zinc-100">
            <div className="h-full bg-red-600" style={{ width: "100%" }} />
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-12" data-testid="case-study-screen">
          <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-3">
            Case study
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
            {caseStudy?.title}
          </h1>

          <div className="mt-6 border-l-4 border-red-600 bg-red-50 rounded-r-md p-5" data-testid="case-study-scenario">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-red-600" />
              <div className="text-xs font-mono uppercase tracking-widest text-red-700">Scenario</div>
            </div>
            <p className="text-zinc-800 leading-relaxed text-sm">
              {caseStudy?.scenario}
            </p>
          </div>

          <div className="mt-8">
            <Label htmlFor="cs-answer" className="text-xs uppercase tracking-wider font-mono">
              Your response
            </Label>
            <Textarea
              id="cs-answer"
              value={caseStudyAnswer}
              onChange={(e) => setCaseStudyAnswer(e.target.value)}
              placeholder="Walk through your complete response step by step. Include PPE choices, immediate actions, reporting, documentation, and any OSHA-required follow-up…"
              rows={14}
              className="mt-2 font-sans text-sm leading-relaxed"
              data-testid="case-study-answer-input"
              disabled={state === "submitting"}
            />
            <div className="mt-2 flex justify-between text-xs text-zinc-500 font-mono">
              <span>Minimum 100 characters recommended · counts for 30% of your final score</span>
              <span data-testid="case-study-char-count">{caseStudyAnswer.length} chars</span>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3 justify-between items-center">
            <Button
              variant="outline"
              onClick={() => setState("running")}
              disabled={state === "submitting"}
              data-testid="case-study-back-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to MCQs
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={state === "submitting"}
                  data-testid="exam-submit-btn"
                >
                  {state === "submitting" ? "Grading with AI…" : "Submit exam"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Submit your exam?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You've answered {answered} of {questions.length} MCQs.
                    {answered < questions.length && " Unanswered questions will be marked incorrect."}
                    {caseStudyAnswer.trim().length < 100
                      ? " Your case study response is short — AI grading works best with a detailed 100-500 word response."
                      : " Your case study will be graded by Claude Sonnet 4.5."}
                    {" "}You cannot change answers after submitting.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="exam-cancel-submit">Keep editing</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={submitExam}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="exam-confirm-submit"
                  >
                    Yes, submit for grading
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {state === "submitting" && (
            <div className="mt-8 text-sm text-zinc-600 font-mono flex items-center gap-2" data-testid="grading-loader">
              <Sparkles className="h-4 w-4 text-red-600 animate-pulse" />
              Claude Sonnet 4.5 is reviewing your case study response…
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <Navbar />

      {/* Progress bar */}
      <div className="border-b border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500" data-testid="exam-progress-indicator">
            Question {currentIdx + 1} of {questions.length}
          </div>
          <div className="flex items-center gap-4">
            {deadline && (() => {
              const remaining = Math.max(0, deadline - now);
              const mm = Math.floor(remaining / 60000);
              const ss = Math.floor((remaining % 60000) / 1000);
              const danger = remaining < 5 * 60 * 1000;
              return (
                <div
                  className={`text-xs font-mono uppercase tracking-widest flex items-center gap-2 ${danger ? "text-red-600 animate-pulse" : "text-zinc-700"}`}
                  data-testid="exam-timer"
                >
                  <Clock className="h-3.5 w-3.5" />
                  <span className="tabular-nums">
                    {String(mm).padStart(2, "0")}:{String(ss).padStart(2, "0")}
                  </span>
                </div>
              );
            })()}
            <div className="text-xs font-mono uppercase tracking-widest text-red-600 flex items-center gap-2">
              {answered}/{questions.length} answered
            </div>
          </div>
        </div>
        <div className="h-1 bg-zinc-100">
          <div
            className="h-full bg-red-600 transition-all"
            style={{ width: `${((currentIdx + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="flex-1 max-w-3xl w-full mx-auto px-6 py-12" data-testid="exam-question-card">
        <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-3">
          Q{currentIdx + 1}
        </div>
        <h2 className="font-display text-2xl lg:text-3xl font-bold leading-tight" data-testid="exam-question-text">
          {current?.question}
        </h2>

        <RadioGroup
          value={answers[current?.id]?.toString() ?? ""}
          onValueChange={(v) => setAnswers((a) => ({ ...a, [current.id]: parseInt(v, 10) }))}
          className="mt-8 space-y-3"
        >
          {current?.options.map((opt, i) => {
            const active = answers[current.id] === i;
            return (
              <label
                key={i}
                htmlFor={`opt-${i}`}
                data-testid={`exam-option-${i}`}
                className={`flex items-start gap-3 border rounded-md p-4 cursor-pointer transition-colors ${
                  active ? "border-red-600 bg-red-50" : "border-zinc-200 bg-white hover:border-zinc-400"
                }`}
              >
                <RadioGroupItem value={i.toString()} id={`opt-${i}`} className="mt-0.5" />
                <div className="flex-1">
                  <span className="font-mono text-xs text-zinc-500 mr-2">{String.fromCharCode(65 + i)}.</span>
                  <span className="text-sm">{opt}</span>
                </div>
              </label>
            );
          })}
        </RadioGroup>
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 border-t border-zinc-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
            disabled={currentIdx === 0}
            data-testid="exam-prev-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Previous
          </Button>

          {currentIdx === questions.length - 1 ? (
            <Button
              onClick={goToProfession}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="exam-to-case-study-btn"
            >
              Continue to case study <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentIdx((i) => Math.min(questions.length - 1, i + 1))}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="exam-next-btn"
            >
              Next <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <footer className="mt-12 bg-zinc-900 py-10">
        <div className="max-w-4xl mx-auto px-6">
          <LegalDisclaimer variant="footer" />
        </div>
      </footer>
    </div>
  );
}
